import React, { FC, memo } from "react";
import { Navigate, Outlet, useRoutes as reactRouterUseRoutes } from "react-router";
import { Spinner } from "@lookiero/aurora";
import { I18n, OnI18nErrorFn } from "@lookiero/i18n-react";
import { Locale } from "@lookiero/sty-psp-locale";
import { Layout as UiLayout } from "@lookiero/sty-psp-ui";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { RafComponent } from "../Root";
import { QueryClientProvider } from "../hooks/useQueryClient";
import { StaticInfoProvider } from "../hooks/useStaticInfo";
import { Session } from "../shared/types";
import { App } from "../views/App";
import { Basket } from "../views/basket/Basket";
import { Categories } from "../views/categories/Categories";
import { Checkout } from "../views/checkout/Checkout";
import { CheckoutConfirmation } from "../views/checkoutConfirmation/CheckoutConfirmation";
import { CompleteYourLook } from "../views/completeYourLook/CompleteYourLook";
import { OfferingLayout } from "../views/offeringLayout/OfferingLayout";
import { Order } from "../views/order/Order";
import { Orders } from "../views/orders/Orders";
import { ProductDetail } from "../views/productDetail/ProductDetail";
import { ReturnConfirmation } from "../views/returnConfirmation/ReturnConfirmation";
import { Sales } from "../views/sales/Sales";
import { TrendingForYou } from "../views/trendingForYou/TrendingForYou";
import { Wishlist } from "../views/wishlist/Wishlist";
import { LookInMiddleware } from "./LookInMiddleware";
import { Routes } from "./routes";

interface RoutingProps {
  readonly basePath?: string;
  readonly session: Session;
  readonly cdnPath: string;
  readonly layout: UiLayout;
  readonly rafComponent: RafComponent;
  readonly tradename: Tradename;
  readonly locale: Locale;
  readonly I18n: I18n;
  readonly apiUrl: () => string;
  readonly onNotAccessible: () => void;
  readonly onI18nError?: OnI18nErrorFn;
  readonly useRedirect: () => Record<string, string>;
  readonly useRoutes: typeof reactRouterUseRoutes;
  readonly onBack: () => void;
  readonly onBackToStyleProfile: () => void;
  readonly onPressLookAndLikeBanner: () => void;
  readonly onShowReturnInstructions: (serviceId: string) => void;
}

const Routing: FC<RoutingProps> = ({
  basePath = "",
  session,
  cdnPath,
  layout,
  rafComponent,
  tradename,
  locale,
  I18n,
  apiUrl,
  onI18nError,
  useRedirect,
  useRoutes = reactRouterUseRoutes,
  onBack,
  onBackToStyleProfile,
  onPressLookAndLikeBanner,
  onShowReturnInstructions,
  onNotAccessible,
}) => {
  return useRoutes([
    {
      path: "",
      element: (
        <QueryClientProvider>
          <StaticInfoProvider
            apiUrl={apiUrl}
            basePath={basePath}
            cdnPath={cdnPath}
            session={session}
            tradename={tradename}
          >
            <I18n loader={<Spinner />} locale={locale} onError={onI18nError}>
              <LookInMiddleware basePath={basePath} onNotAccessible={onNotAccessible}>
                <App>
                  <Outlet />
                </App>
              </LookInMiddleware>
            </I18n>
          </StaticInfoProvider>
        </QueryClientProvider>
      ),
      children: [
        {
          element: <OfferingLayout layout={layout} />,
          children: [
            {
              path: Routes.COMPLETE_YOUR_LOOK,
              element: <CompleteYourLook onBack={onBack} onBackToStyleProfile={onBackToStyleProfile} />,
            },
            {
              path: Routes.LOOKS_BY_ANCHOR_GARMENT,
              element: <CompleteYourLook onBack={onBack} onBackToStyleProfile={onBackToStyleProfile} />,
            },
            {
              path: Routes.CATEGORIES,
              element: <Categories onBack={onBack} />,
            },
            {
              path: Routes.CATEGORY,
              element: <Categories onBack={onBack} />,
            },
            {
              path: Routes.SALES,
              element: <Sales onBack={onBack} />,
            },
            {
              path: Routes.SALE,
              element: <Sales onBack={onBack} />,
            },
            {
              path: Routes.TRENDING_FOR_YOU,
              element: <TrendingForYou onBack={onBack} onBackToStyleProfile={onBackToStyleProfile} />,
            },
          ],
        },
        {
          path: Routes.CHECKOUT,
          element: <Checkout layout={layout} useRedirect={useRedirect} />,
        },
        {
          path: Routes.CHECKOUT_CONFIRMATION,
          element: (
            <CheckoutConfirmation
              layout={layout}
              rafComponent={rafComponent}
              onPressLookAndLikeBanner={onPressLookAndLikeBanner}
            />
          ),
        },
        {
          path: Routes.ORDER,
          element: <Order layout={layout} onShowReturnInstructions={onShowReturnInstructions} />,
        },
        {
          path: Routes.ORDERS,
          element: <Orders layout={layout} />,
        },
        {
          path: Routes.PRODUCT_LOOK_DETAIL,
          element: <ProductDetail layout={layout} />,
        },
        {
          path: Routes.PRODUCT_CATEGORY_DETAIL,
          element: <ProductDetail layout={layout} />,
        },
        {
          path: Routes.PRODUCT_DETAIL,
          element: <ProductDetail layout={layout} />,
        },
        {
          path: Routes.RETURN_CONFIRMATION,
          element: <ReturnConfirmation layout={layout} onShowReturnInstructions={onShowReturnInstructions} />,
        },
        {
          path: Routes.WISHLIST,
          element: <Wishlist layout={layout} />,
        },
        {
          path: Routes.CART,
          element: <Basket layout={layout} />,
        },
        {
          path: "*",
          element: <Navigate to={`${Routes.HOME}`} replace />,
        },
      ],
    },
  ]);
};

/**
 * Provided useRoutes is not stable (when integrated with Sentry) as it's rendering a different component tree.
 *
 * https://github.com/getsentry/sentry-javascript/blob/master/packages/react/src/reactrouterv6.tsx#L221
 * (SentryRoutes is a new component after each re-render)
 */
const MemoizedRouting = memo(Routing);
export { MemoizedRouting as Routing };
